import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Bar, BarChart, Cell, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useDashboard } from "../lib/api";
import { fmt, fmtMoney } from "../lib/format";
import { KpiCard, Loading, PageHeader } from "../components/PageHeader";
import { SourceBadge, StatusBadge } from "../components/Badges";

const DohChart = ({ products, target }) => {
  const data = products
    .filter((p) => p.doh !== null && p.site_count > 0)
    .map((p) => ({ name: p.code, doh: Math.min(Math.floor(p.doh), 120) }));
  return (
    <div data-testid="doh-chart" className="h-72 w-full">
      <ResponsiveContainer>
        <BarChart data={data} margin={{ left: -20, right: 8, top: 8 }}>
          <XAxis dataKey="name" tick={{ fill: "#64748b", fontSize: 10 }} interval={0} angle={-35} textAnchor="end" height={60} />
          <YAxis tick={{ fill: "#64748b", fontSize: 10 }} />
          <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", color: "hsl(var(--foreground))", fontSize: 12 }} cursor={{ fill: "hsl(var(--muted))" }} />
          <ReferenceLine y={target} stroke="#f59e0b" strokeDasharray="4 4" />
          <Bar dataKey="doh" radius={[2, 2, 0, 0]}>
            {data.map((d) => <Cell key={d.name} fill={d.doh < target ? "#ef4444" : "#0ea5e9"} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default function Overview() {
  const { data, isLoading } = useDashboard();
  if (isLoading || !data) return <Loading />;
  const { kpis, products, rows } = data;
  const lowRows = rows.filter((r) => r.status === "low").slice(0, 8);
  return (
    <div data-testid="overview-page">
      <PageHeader kicker={`Forecast ${data.week_label || "—"}`} title="Stock Overview">
        <Link to="/stock" data-testid="overview-go-stock" className="flex items-center gap-2 rounded-md bg-sky-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-sky-500">
          Manage stock holding <ArrowRight className="h-4 w-4" />
        </Link>
      </PageHeader>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <KpiCard label="Total stock (units)" value={fmt(kpis.total_stock)} testId="kpi-total-stock" />
        <KpiCard label="Stock value" value={fmtMoney(kpis.total_value)} sub={kpis.products_missing_cost ? `${kpis.products_missing_cost} products without a unit cost` : "All products costed"} testId="kpi-total-value" tone="sky" delay={60} />
        <KpiCard label="Our stock overrides" value={kpis.overrides} sub={`of ${kpis.products} products`} testId="kpi-overrides" tone="amber" delay={120} />
        <KpiCard label={`Products below ${kpis.target_doh} DOH`} value={kpis.low_products} testId="kpi-low-products" tone={kpis.low_products ? "red" : "green"} delay={180} />
        <KpiCard label="Low FC lines, no reason" value={kpis.low_rows_missing_reason} sub={`${kpis.low_rows} low of ${kpis.rows} FC lines`} testId="kpi-missing-reasons" tone={kpis.low_rows_missing_reason ? "red" : "green"} delay={240} />
      </div>
      <div className="mt-8 grid grid-cols-1 gap-6 xl:grid-cols-5">
        <section className="rise rounded-lg border border-slate-800 bg-[#131b29]/80 p-6 xl:col-span-3">
          <h2 className="font-display text-lg font-semibold uppercase tracking-wide">Days on hand by product</h2>
          <p className="mb-4 text-xs text-slate-500">Dashed line = {kpis.target_doh}-day Amazon target</p>
          <DohChart products={products} target={kpis.target_doh} />
        </section>
        <section className="rise rounded-lg border border-slate-800 bg-[#131b29]/80 p-6 xl:col-span-2">
          <h2 className="font-display text-lg font-semibold uppercase tracking-wide">Low FC lines</h2>
          <p className="mb-4 text-xs text-slate-500">Amazon needs a reason code for each</p>
          {lowRows.length === 0 ? (
            <div data-testid="no-low-rows" className="py-10 text-sm text-emerald-400">Every FC line meets the target.</div>
          ) : (
            <ul className="space-y-2">
              {lowRows.map((r) => (
                <li key={r.key} data-testid={`low-row-${r.key}`} className="flex items-center justify-between gap-3 rounded-md border border-slate-800 bg-slate-900/50 px-3 py-2">
                  <div className="min-w-0">
                    <div className="num truncate text-xs text-slate-200">{r.item} · {r.site}</div>
                    <div className="truncate text-[11px] text-slate-500">{r.reason_for_low_doh || "No reason selected"}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="num text-sm text-red-400">{fmt(r.doh)}d</span>
                    <SourceBadge source={r.source} />
                  </div>
                </li>
              ))}
            </ul>
          )}
          <Link to="/report" data-testid="overview-go-report" className="mt-4 inline-flex items-center gap-1 text-sm text-sky-400 hover:text-sky-300">
            Open Amazon report <ArrowRight className="h-4 w-4" />
          </Link>
          <div className="mt-6 hidden"><StatusBadge status="ok" /></div>
        </section>
      </div>
    </div>
  );
}
